import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Checkbox from '@mui/joy/Checkbox';
import { attributesApi } from '../../api/attributes.api';
import { AttributeDataType } from '../../types/attribute';

const AttributeForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    dataType: 'text' as AttributeDataType,
    isFilterable: false,
    isVariantAxis: false,
    isRequired: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await attributesApi.create(formData);
      navigate('/attributes');
    } catch (error) {
      console.error('Error creating attribute:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        Nuevo Atributo
      </Typography>

      <Sheet variant="outlined" component="form" onSubmit={handleSubmit}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Nombre</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Color"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Código</FormLabel>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
                placeholder="Ej: color"
              />
              <FormHelperText>Identificador único en minúsculas (sin espacios)</FormHelperText>
            </FormControl>

            <FormControl required>
              <FormLabel>Tipo de Dato</FormLabel>
              <Select
                value={formData.dataType}
                onChange={(_, value) => setFormData({ ...formData, dataType: value as AttributeDataType })}
              >
                <Option value="text">Texto</Option>
                <Option value="textarea">Texto largo</Option>
                <Option value="number">Número</Option>
                <Option value="boolean">Booleano</Option>
                <Option value="date">Fecha</Option>
                <Option value="select">Selección</Option>
                <Option value="multiselect">Múltiple selección</Option>
                <Option value="gallery">Galería</Option>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Checkbox
                label="Es eje de variante"
                checked={formData.isVariantAxis}
                onChange={(e) => setFormData({ ...formData, isVariantAxis: e.target.checked })}
              />
              <Checkbox
                label="Es filtrable"
                checked={formData.isFilterable}
                onChange={(e) => setFormData({ ...formData, isFilterable: e.target.checked })}
              />
              <Checkbox
                label="Es requerido"
                checked={formData.isRequired}
                onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.level1',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="outlined" color="neutral" onClick={() => navigate('/attributes')}>
            Cancelar
          </Button>
          <Button type="submit" color="primary" loading={loading}>
            Crear
          </Button>
        </Box>
      </Sheet>
    </Box>
  );
};

export default AttributeForm;
